/* ============================================================================
   Ohr Labs — Cookie consent (cookie-consent.js)
   ----------------------------------------------------------------------------
   Drives the custom banner (snippets/cookie-banner.liquid) through Shopify's
   Customer Privacy API so consent actually gates cookies. Prior-consent model:
   Analytics + Marketing default OFF; Necessary always on. Banner shows only when
   Shopify's shouldShowBanner() is true (UK/EU/regulated regions); a persistent
   re-open pill lets the visitor change/withdraw consent anytime.

   Structured for testing: pure helpers (mapConsent/readConsent/hasDecision) and
   initBanner(els, api, win) take injected dependencies, so tests/cookie-consent.test.mjs
   can exercise every path with a mock Customer Privacy API and mock DOM. The
   browser bootstrap at the bottom only runs when a real `document` exists.
   ============================================================================ */
(function () {
  // ---- Pure helpers (no DOM) -------------------------------------------------

  // The single consent shape sent to Shopify. `preferences` follows Analytics;
  // sale_of_data stays false (US/CCPA path not offered to UK/EU visitors).
  function mapConsent(analytics, marketing) {
    return {
      analytics: !!analytics,
      marketing: !!marketing,
      preferences: !!analytics,
      sale_of_data: false
    };
  }

  function truthyConsent(v) { return v === true || v === 'yes' || v === 1; }

  // Normalise Shopify's currentVisitorConsent() (values are ''/'yes'/'no' or bools).
  function readConsent(cur) {
    cur = cur || {};
    return { analytics: truthyConsent(cur.analytics), marketing: truthyConsent(cur.marketing) };
  }

  // Has the visitor already made a choice? ('' = undecided; 'yes'/'no' = decided.)
  function hasDecision(cur) {
    if (!cur) return false;
    return [cur.analytics, cur.marketing, cur.preferences, cur.sale_of_data].some(function (v) {
      return v === 'yes' || v === 'no' || v === true;
    });
  }

  // ---- Banner controller (DOM + API injected) --------------------------------

  function initBanner(els, api, win) {
    win = win || (typeof window !== 'undefined' ? window : {});
    var raf = (win && win.requestAnimationFrame) ? win.requestAnimationFrame.bind(win) : function (f) { f(); };
    var decided = false; // per-open guard so one decision fires setTrackingConsent once

    function on(el, type, fn) { if (el && el.addEventListener) el.addEventListener(type, fn); }
    function setLayer(n) { els.ck.setAttribute('data-layer', String(n)); }
    function currentLayer() { return parseInt(els.ck.getAttribute('data-layer'), 10) || 1; }
    function swOn(sw) { return !!sw && sw.getAttribute('aria-checked') === 'true'; }
    function setSw(sw, on2) { if (sw) sw.setAttribute('aria-checked', on2 ? 'true' : 'false'); }
    function flip(sw) { if (!sw || sw.getAttribute('aria-disabled') === 'true') return; setSw(sw, !swOn(sw)); }
    function focusEl(el) { if (el && el.focus) { try { el.focus(); } catch (e) {} } }

    function safeCurrentRaw() {
      try { return api && api.currentVisitorConsent ? api.currentVisitorConsent() : null; } catch (e) { return null; }
    }
    function shouldShow() {
      // Belt-and-braces: if the visitor already has a recorded decision, never show —
      // don't rely on shouldShowBanner() alone (it can lag/misreport right after a save).
      if (hasDecision(safeCurrentRaw())) return false;
      try { return !!(api && api.shouldShowBanner && api.shouldShowBanner()); } catch (e) { return false; }
    }
    function prefillToggles() {
      var c = readConsent(safeCurrentRaw());
      setSw(els.analyticsSw, c.analytics);
      setSw(els.marketingSw, c.marketing);
    }

    // Snap the card's BOTTOM edge onto the grid line (design's fitWidth) — the
    // viewport height is rarely a whole number of cells, so nudge the card up by
    // the remainder. Guarded so it no-ops in the test (no layout engine).
    function cellPx() {
      try {
        var n = parseFloat(win.getComputedStyle(win.document.documentElement).getPropertyValue('--cell'));
        return (n && n > 0) ? n : 40;
      } catch (e) { return 40; }
    }
    function fitWidth() {
      var el = els.ck;
      if (!el || !el.style || !el.getBoundingClientRect || !win.getComputedStyle) return;
      el.style.removeProperty('transform');
      var cell = cellPx();
      var off = ((el.getBoundingClientRect().bottom % cell) + cell) % cell;
      if (off > 0.5) el.style.transform = 'translateY(' + (-off) + 'px)';
    }

    function open() {
      decided = false;
      prefillToggles();
      setLayer(1);
      els.scrim.removeAttribute('hidden');
      raf(function () { els.scrim.classList.add('open'); fitWidth(); focusEl(els.reject); });
    }
    function close() {
      els.scrim.classList.remove('open');
      // hide after the fade so the transition can play (harmless if it doesn't)
      setTimeout(function () {
        if (!els.scrim.classList.contains('open')) els.scrim.setAttribute('hidden', '');
      }, 260);
    }

    function applyConsent(analytics, marketing) {
      if (decided) return;         // ignore double-clicks within one open
      decided = true;
      var record = mapConsent(analytics, marketing);
      try {
        if (api && api.setTrackingConsent) api.setTrackingConsent(record, function () {});
      } catch (e) { /* fail-safe: never break the page */ }
      close();
    }

    // Wire controls
    on(els.accept, 'click', function () { applyConsent(true, true); });
    on(els.reject, 'click', function () { applyConsent(false, false); });
    on(els.manage, 'click', function () { setLayer(2); focusEl(els.back); });
    on(els.back, 'click', function () { setLayer(1); focusEl(els.manage); });
    on(els.save, 'click', function () { applyConsent(swOn(els.analyticsSw), swOn(els.marketingSw)); });
    [els.analyticsSw, els.marketingSw].forEach(function (sw) {
      on(sw, 'click', function () { flip(sw); });
      on(sw, 'keydown', function (e) {
        if (e && (e.key === 'Enter' || e.key === ' ' || e.key === 'Spacebar')) {
          if (e.preventDefault) e.preventDefault();
          flip(sw);
        }
      });
    });
    on(els.reopen, 'click', function (e) { if (e && e.preventDefault) e.preventDefault(); open(); });
    // Escape inside the dialog steps Layer 2 back to Layer 1; it never dismisses
    // Layer 1 (closing without a choice must not imply consent).
    on(els.scrim, 'keydown', function (e) {
      if (e && e.key === 'Escape' && currentLayer() === 2) setLayer(1);
    });
    // Re-snap the card's bottom edge to the grid when the width settles.
    if (win && win.addEventListener) {
      var rzT;
      win.addEventListener('resize', function () {
        clearTimeout(rzT);
        rzT = setTimeout(function () { if (els.scrim.classList.contains('open')) fitWidth(); }, 140);
      });
    }

    function start() {
      prefillToggles();
      // Show the banner only for regulated regions with no decision yet. Otherwise
      // show nothing — the footer "Cookie preferences" link is the always-available
      // way to (re)open and change/withdraw consent (no persistent floating pill).
      if (shouldShow()) open();
    }

    return { start: start, open: open, close: close, applyConsent: applyConsent, setLayer: setLayer };
  }

  // ---- Resolve the banner elements from a document ---------------------------
  function resolveEls(doc) {
    var scrim = doc.getElementById('ck-scrim');
    var ck = doc.getElementById('ck');
    if (!scrim || !ck) return null;
    return {
      scrim: scrim,
      ck: ck,
      reopen: doc.getElementById('ck-reopen'),
      accept: ck.querySelector('[data-accept]'),
      reject: ck.querySelector('[data-reject]'),
      manage: ck.querySelector('[data-manage]'),
      back: ck.querySelector('[data-back]'),
      save: ck.querySelector('[data-save]'),
      analyticsSw: ck.querySelector('[data-toggle="analytics"]'),
      marketingSw: ck.querySelector('[data-toggle="marketing"]')
    };
  }

  // ---- Exports for tests -----------------------------------------------------
  if (typeof module !== 'undefined' && module.exports) {
    module.exports = { mapConsent: mapConsent, readConsent: readConsent, hasDecision: hasDecision, initBanner: initBanner, resolveEls: resolveEls };
  }

  // ---- Browser bootstrap -----------------------------------------------------
  if (typeof document === 'undefined') return;

  function getApi() { return (window.Shopify && window.Shopify.customerPrivacy) || null; }

  function whenApiReady(cb) {
    var done = false;
    function fire() { if (done) return; var a = getApi(); if (a) { done = true; cb(a); } }
    // Online Store themes MUST explicitly load the feature before the API is fully
    // usable (per shopify.dev/docs/api/customer-privacy). Using a pre-existing
    // window.Shopify.customerPrivacy WITHOUT loadFeatures can show the banner yet
    // fail to persist setTrackingConsent — so always loadFeatures first, don't
    // short-circuit on the object already being present.
    if (window.Shopify && typeof window.Shopify.loadFeatures === 'function') {
      window.Shopify.loadFeatures([{ name: 'consent-tracking-api', version: '0.1' }], function (error) {
        if (!error) fire();
      });
    } else if (getApi()) {
      fire();
    }
    // Belt-and-braces: also fire when Shopify signals consent state is ready/changed.
    document.addEventListener('visitorConsentCollected', fire);
    var tries = 0;
    var iv = setInterval(function () { tries++; if (getApi()) { clearInterval(iv); fire(); } else if (tries > 40) clearInterval(iv); }, 250);
  }

  function boot() {
    var els = resolveEls(document);
    if (!els) return;
    whenApiReady(function (api) { initBanner(els, api, window).start(); });
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
})();
